import {
  formatMetric,
  useCampuses,
  useDashboard,
  useNeedsYou,
  useOnboarding,
  useAccess,
  useRefreshInsights,
  useSession,
} from '@church/data'
import { timeAgo } from '@church/domain'
import {
  faArrowDown,
  faArrowRight,
  faArrowUp,
  faChartLine,
  faCheck,
  faChevronRight,
  faTableList,
} from '@church/ui-icons'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { WeeklyBars } from '../components/insights/WeeklyBars.tsx'
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  ErrorState,
  Icon,
  ListSkeleton,
  PageHeader,
  SidePanel,
} from '../components/ui.tsx'
import { useCountUp } from '../lib/count-up.ts'

export const Route = createFileRoute('/_authed/_shell/')({
  component: DashboardScreen,
})

/**
 * Dashboard home: the last twelve weeks of each number this person may see,
 * from the read model — attendance, check-ins, groups, new people and, for
 * those who hold giving access, giving. Each card compares the last four
 * weeks with the four before; choosing one opens its numbers, week by week,
 * in a panel. "All numbers" is every metric as one table.
 */
function DashboardScreen() {
  const { me } = useSession()
  const access = useAccess()
  const [campusId, setCampusId] = useState<string | null>(null)
  const dashboard = useDashboard(campusId)
  const onboarding = useOnboarding()
  const campuses = useCampuses()
  const refresh = useRefreshInsights()
  // `all` is the every-metric table; otherwise a metric's key.
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [shownKey, setShownKey] = useState<string>('all')
  const open = (key: string) => {
    setShownKey(key)
    setOpenKey(key)
  }
  const firstName = me?.user.name.split(' ')[0]
  const needsYou = useNeedsYou()
  const metrics = dashboard.data?.metrics ?? []
  // The hero: attendance when this person may see it, else the first number.
  const hero = metrics.find((m) => m.metric === 'service_attendance') ?? metrics[0]
  const rest = metrics.filter((m) => m !== hero)

  return (
    <div className="space-y-6">
      <PageHeader title={`Welcome back${firstName ? `, ${firstName}` : ''}`} visible />
      {(campuses.data?.length ?? 0) > 1 && (
        <div
          role="radiogroup"
          aria-label="Campus"
          className="-mx-gutter flex gap-2 overflow-x-auto px-gutter no-scrollbar sm:mx-0 sm:px-0"
        >
          {[{ id: null, name: 'Whole church' }, ...(campuses.data ?? [])].map((campus) => {
            const chosen = campusId === campus.id
            return (
              <button
                key={campus.id ?? 'all'}
                type="button"
                role="radio"
                aria-checked={chosen}
                onClick={() => setCampusId(campus.id)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-(--duration-fast) ${
                  chosen
                    ? 'bg-content-default text-surface-base'
                    : 'bg-surface-sunken text-content-muted hover:text-content-default'
                }`}
              >
                {campus.name}
              </button>
            )
          })}
        </div>
      )}
      {needsYou.length > 0 && (
        <nav aria-label="Needs you" className="flex flex-wrap gap-2">
          {needsYou.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium no-underline transition-colors duration-(--duration-fast) ${
                item.tone === 'danger'
                  ? 'bg-danger-soft text-danger-content'
                  : item.tone === 'warning'
                    ? 'bg-warning-soft text-warning-content'
                    : 'bg-brand-soft text-content-brand'
              }`}
            >
              {item.label}
              <Icon icon={faChevronRight} className="size-3" />
            </Link>
          ))}
        </nav>
      )}
      {onboarding.show && (
        <Card className="space-y-3 p-4 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold text-content-default">Getting started</h2>
            <p className="text-sm text-content-muted">
              {onboarding.steps.length - onboarding.remaining} of {onboarding.steps.length} done
            </p>
          </div>
          <ol className="divide-y divide-border-subtle">
            {onboarding.steps.map((step) => (
              <li key={step.key} className="flex items-start gap-3 py-3">
                <span
                  aria-hidden
                  className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    step.done
                      ? 'bg-success-solid text-success-on-solid'
                      : 'border-2 border-border-strong'
                  }`}
                >
                  {step.done && <Icon icon={faCheck} className="size-3" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-medium ${step.done ? 'text-content-muted line-through' : 'text-content-default'}`}
                  >
                    {step.title}
                    <span className="sr-only">{step.done ? ' (done)' : ' (to do)'}</span>
                  </p>
                  {!step.done && <p className="text-sm text-content-muted">{step.detail}</p>}
                </div>
                {!step.done && (
                  <Link
                    to={step.to}
                    className="inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-content-brand hover:bg-surface-sunken"
                  >
                    Start
                    <Icon icon={faArrowRight} />
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </Card>
      )}
      {dashboard.isPending ? (
        <Card>
          <ListSkeleton rows={4} />
        </Card>
      ) : !dashboard.data ? (
        <ErrorState
          error={dashboard.error}
          safe="Nothing was changed: the figures are safe, and the rest of ForChurch is working."
          retrying={dashboard.isRefetching}
          onRetry={() => void dashboard.refetch()}
        />
      ) : !hero ? (
        <Card>
          <EmptyState
            title="No figures yet"
            hint="Attendance, check-ins, groups and giving show here as they are recorded."
          />
        </Card>
      ) : (
        <>
          <Hero metric={hero} onOpen={() => open(hero.metric)} />
          {rest.length > 0 && (
            /* On a phone the other numbers scroll sideways, one card and a bit at a time. */
            <div className="-mx-gutter flex snap-x snap-mandatory gap-3 overflow-x-auto px-gutter pb-1 no-scrollbar sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 xl:grid-cols-3">
              {rest.map((m) => (
                <Card key={m.metric} className="w-72 shrink-0 snap-start overflow-hidden sm:w-auto">
                  <button
                    type="button"
                    onClick={() => open(m.metric)}
                    aria-label={`${m.label}: open the week-by-week numbers`}
                    className="block w-full space-y-3 p-5 text-left transition-colors duration-(--duration-fast) hover:bg-surface-sunken active:bg-surface-heading"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-medium tracking-wide text-content-subtle uppercase">
                          {m.label}
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-content-default tabular-nums">
                          {formatMetric(m.metric, m.lastFourWeeks)}
                        </p>
                        <p className="text-xs text-content-muted">last 4 full weeks</p>
                      </div>
                      <Change value={m.change} />
                    </div>
                    <WeeklyBars
                      series={m.series}
                      label={m.label}
                      format={(v) => formatMetric(m.metric, v)}
                    />
                  </button>
                </Card>
              ))}
            </div>
          )}
          <ErrorNote
            error={refresh.error}
            onRetry={() => refresh.mutate()}
            retrying={refresh.isPending}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-content-muted">
            <span className="flex items-center gap-1">
              {dashboard.data.asOf
                ? `Updated ${timeAgo(new Date(dashboard.data.asOf))}`
                : 'Figures are being prepared'}
              {access.has('reports', 'viewer') && (
                <>
                  <span aria-hidden>·</span>
                  <Button
                    variant="ghost"
                    className="px-2"
                    pending={refresh.isPending}
                    pendingLabel="Refreshing…"
                    onClick={() => refresh.mutate()}
                  >
                    Refresh
                  </Button>
                </>
              )}
            </span>
            <span className="flex items-center gap-1">
              <Button variant="ghost" icon={faTableList} onClick={() => open('all')}>
                All numbers
              </Button>
              {access.has('reports', 'viewer') && (
                <Link
                  to="/reports"
                  className="inline-flex items-center gap-2 rounded-control px-3 py-2 text-sm font-medium text-content-brand no-underline hover:bg-surface-sunken"
                >
                  <Icon icon={faChartLine} />
                  Reports
                </Link>
              )}
            </span>
          </div>
          <MetricPanel
            metrics={dashboard.data.metrics}
            openKey={openKey}
            shownKey={shownKey}
            onClose={() => setOpenKey(null)}
          />
        </>
      )}
    </div>
  )
}

type Metric = NonNullable<ReturnType<typeof useDashboard>['data']>['metrics'][number]

/**
 * The one number that matters most, large: its value counting up into
 * place, which way it moved, and its twelve weeks under it. Opens the
 * week-by-week numbers.
 */
function Hero({ metric, onOpen }: { metric: Metric; onOpen: () => void }) {
  const value = useCountUp(metric.lastFourWeeks)
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${metric.label}: open the week-by-week numbers`}
        className="block w-full space-y-4 p-5 text-left transition-colors duration-(--duration-fast) hover:bg-surface-sunken active:bg-surface-heading sm:p-6"
      >
        <div>
          <p className="text-sm font-medium text-content-muted">
            {metric.label} · last 4 full weeks
          </p>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-content-default tabular-nums sm:text-5xl">
            {formatMetric(metric.metric, value)}
          </p>
          <div className="mt-2">
            <Change value={metric.change} />
          </div>
        </div>
        <WeeklyBars
          tall
          series={metric.series}
          label={metric.label}
          format={(v) => formatMetric(metric.metric, v)}
        />
      </button>
    </Card>
  )
}

/** One metric's twelve weeks — the bars, larger, and every number — or all of them as a table. */
function MetricPanel({
  metrics,
  openKey,
  shownKey,
  onClose,
}: {
  metrics: Metric[]
  openKey: string | null
  shownKey: string
  onClose: () => void
}) {
  const metric = metrics.find((m) => m.metric === shownKey)
  const weekOf = (week: string) =>
    new Date(`${week}T00:00:00Z`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  return (
    <SidePanel
      open={openKey !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={metric ? metric.label : 'All numbers'}
      description={metric ? 'The last twelve weeks' : undefined}
    >
      {metric ? (
        <>
          <div className="flex items-end justify-between gap-2">
            <div>
              <p className="text-3xl font-bold tabular-nums text-content-default">
                {formatMetric(metric.metric, metric.lastFourWeeks)}
              </p>
              <p className="text-xs text-content-muted">last 4 full weeks</p>
            </div>
            <Change value={metric.change} />
          </div>
          <WeeklyBars
            tall
            series={metric.series}
            label={metric.label}
            format={(v) => formatMetric(metric.metric, v)}
          />
          <table className="w-full text-sm">
            <caption className="sr-only">{metric.label}, week by week</caption>
            <thead className="text-left text-xs uppercase tracking-wide text-content-muted">
              <tr>
                <th className="py-2 font-medium">Week of</th>
                <th className="py-2 text-right font-medium">{metric.label}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {[...metric.series].reverse().map((row) => (
                <tr key={row.week}>
                  <td className="py-2 text-content-default">{weekOf(row.week)}</td>
                  <td className="py-2 text-right tabular-nums text-content-default">
                    {formatMetric(metric.metric, row.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5">
          <table className="w-full text-sm">
            <caption className="sr-only">Every metric, week by week</caption>
            <thead className="text-left text-xs uppercase tracking-wide text-content-muted">
              <tr>
                <th className="py-2 pr-3 font-medium">Week of</th>
                {metrics.map((m) => (
                  <th key={m.metric} className="px-2 py-2 text-right font-medium">
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {metrics[0]?.series.map((row, i) => (
                <tr key={row.week}>
                  <td className="py-2 pr-3 whitespace-nowrap text-content-default">
                    {weekOf(row.week)}
                  </td>
                  {metrics.map((m) => (
                    <td
                      key={m.metric}
                      className="px-2 py-2 text-right tabular-nums text-content-muted"
                    >
                      {formatMetric(m.metric, m.series[i]?.value ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SidePanel>
  )
}

/** Up or down with an arrow and words — never colour alone. */
function Change({ value }: { value: number | null }) {
  if (value === null) return <span className="text-xs text-content-subtle">new</span>
  const up = value > 0
  const flat = value === 0
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        flat
          ? 'bg-surface-sunken text-content-muted'
          : up
            ? 'bg-success-soft text-success-content'
            : 'bg-warning-soft text-warning-content'
      }`}
      title="Compared with the four weeks before"
    >
      <Icon icon={flat ? faArrowRight : up ? faArrowUp : faArrowDown} className="size-3" />
      {flat ? 'level' : `${Math.abs(value)}% ${up ? 'up' : 'down'}`}
    </span>
  )
}
